import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Share2, Copy, Trash2, Lock, Eye, Globe } from "lucide-react";

export default function ShareLinkDialog({ scope, hiveId, label = "Share" }) {
  const [open, setOpen] = useState(false);
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pwd, setPwd] = useState("");
  const [usePwd, setUsePwd] = useState(false);
  const [expires, setExpires] = useState("");

  const myFilter = (s) => scope === "hive" ? (s.scope === "hive" && s.target_id === hiveId) : (s.scope === "apiary");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/share/links");
      setLinks(data.filter(myFilter));
    } finally { setLoading(false); }
  };
  useEffect(() => { if (open) load(); }, [open]);

  const create = async () => {
    try {
      await api.post("/share/links", {
        scope,
        target_id: scope === "hive" ? hiveId : undefined,
        password: usePwd && pwd ? pwd : undefined,
        expires_in_days: expires ? Number(expires) : undefined,
      });
      setPwd(""); setUsePwd(false); setExpires("");
      await load();
      toast.success("Share link created");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const revoke = async (id) => {
    if (!window.confirm("Revoke this share link?")) return;
    try {
      await api.delete(`/share/links/${id}`);
      await load();
      toast.success("Link revoked");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const publicUrl = (slug) => `${window.location.origin}/share/${slug}`;
  const copy = (slug) => { navigator.clipboard.writeText(publicUrl(slug)); toast.success("Link copied"); };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="rounded-full border-stone-300" data-testid={`open-share-${scope}`}>
          <Share2 className="w-4 h-4 mr-1.5" /> {label}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-medium flex items-center gap-2">
            {scope === "apiary" ? <Globe className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            Share {scope === "apiary" ? "your apiary" : "this hive"}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3 max-h-[60vh] overflow-y-auto">
          <p className="text-sm text-stone-600">
            Anyone with the link can view read-only. Optional password adds extra protection.
          </p>

          <div className="rounded-xl border border-stone-200 p-4 space-y-3" data-testid={`share-new-${scope}`}>
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <Lock className="w-4 h-4 text-stone-500" />
                <span className="text-sm font-medium">Require a password</span>
              </div>
              <Switch checked={usePwd} onCheckedChange={setUsePwd} data-testid={`share-pwd-switch-${scope}`} />
            </div>
            {usePwd && (
              <div>
                <Label className="text-xs uppercase tracking-[0.12em] text-stone-500">Password</Label>
                <Input value={pwd} onChange={(e) => setPwd(e.target.value)} placeholder="Something memorable" className="mt-1" data-testid={`share-pwd-input-${scope}`} />
              </div>
            )}
            <div>
              <Label className="text-xs uppercase tracking-[0.12em] text-stone-500">Expires after (days, optional)</Label>
              <Input type="number" min={1} value={expires} onChange={(e) => setExpires(e.target.value)} placeholder="Leave empty for never" className="mt-1" data-testid={`share-expires-${scope}`} />
            </div>
            <Button onClick={create} className="rounded-full bg-yellow-400 text-amber-900 hover:bg-yellow-300 w-full" data-testid={`share-create-${scope}`}>
              Create new link
            </Button>
          </div>

          <div className="text-xs font-semibold uppercase tracking-[0.18em] text-stone-500 mt-3">Existing links</div>
          {loading ? <p className="text-sm text-stone-500">Loading…</p> : links.length === 0 ? (
            <p className="text-sm text-stone-500">None yet.</p>
          ) : (
            <ul className="space-y-2">
              {links.map((l) => (
                <li key={l.id} className={`p-3 rounded-xl border ${l.revoked ? "border-stone-200 bg-stone-50 opacity-60" : "border-stone-200 bg-white"}`} data-testid={`share-row-${l.id}`}>
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <code className="text-xs font-mono text-stone-700 break-all">{publicUrl(l.slug)}</code>
                    <div className="flex items-center gap-1">
                      {l.has_password && <span className="text-[10px] uppercase tracking-[0.12em] text-amber-700 px-1.5 py-0.5 rounded bg-amber-50 border border-amber-200">PWD</span>}
                      <Button size="icon" variant="ghost" onClick={() => copy(l.slug)} disabled={l.revoked} className="h-7 w-7" data-testid={`share-copy-${l.id}`}>
                        <Copy className="w-3.5 h-3.5" />
                      </Button>
                      <Button size="icon" variant="ghost" onClick={() => revoke(l.id)} disabled={l.revoked} className="h-7 w-7 text-stone-400 hover:text-red-600" data-testid={`share-revoke-${l.id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                  <div className="text-[10px] uppercase tracking-[0.15em] text-stone-400 mt-1.5">
                    {l.revoked ? "Revoked" : `${l.view_count} view${l.view_count === 1 ? "" : "s"}`}
                    {l.expires_at && ` · expires ${new Date(l.expires_at).toLocaleDateString()}`}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)} className="rounded-full">Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
